import {
  Button,
  OutlinedSelect,
  SelectOptionType,
  TextOutlinedInput,
  addToast,
} from '@octano/global-ui';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { Col, Container, Form, Row } from 'reactstrap';

import { useIdentificationTypeLoader } from '../../../../components/loaders/IdentificationTypeLoader';
import { PathsLayouts } from '../../../../config/routes';
import { useParameters } from '../../../../hooks/useParameters';
import { useValidations } from '../../../../hooks/useValidations';
import { IdentificationDocumentCode } from '../../../../utils/enums/identification-document-code.enum';
import searchStudent from '../api';

interface SearchUserForm {
  identificationType: SelectOptionType | null;
  identification: string;
  country?: SelectOptionType | null;
}

const DEFAULT_VALUES: SearchUserForm = {
  identificationType: null,
  identification: '',
  country: null,
};

const keyPrefix = 'financialPortal.currentAccount.search';

export default function StudentSearchAndDetail() {
  const { t } = useTranslation();
  const history = useHistory();
  const { validateRut, msgValidations } = useValidations();
  const { countryOptions } = useParameters();
  const { data: identificationTypeData } = useIdentificationTypeLoader();
  const [isSearching, setIsSearching] = useState(false);

  const {
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState,
  } = useForm<SearchUserForm>({
    defaultValues: { ...DEFAULT_VALUES },
    mode: 'onChange',
  });

  const identificationType = watch('identificationType');
  const identification = watch('identification');

  // Normalise the identification value the same way the teacher search does
  // (strip dots/dashes, uppercase) so the RUT validator and backend lookup
  // work on a canonical form.
  useEffect(() => {
    const newValue = identification
      ?.replace('.', '')
      .replace('-', '')
      .toUpperCase()
      .trim();
    if (newValue !== identification) {
      setValue('identification', newValue);
    }
  }, [setValue, identification]);

  // Country is only relevant when the selected tipo-ID is passport — clear
  // it otherwise so stale values are not sent to the backend.
  useEffect(() => {
    if (identificationType?.value !== IdentificationDocumentCode.Passport) {
      setValue('country', null);
    }
  }, [setValue, identificationType]);

  const dynamicValidate = useCallback(
    (value: string | SelectOptionType | null | undefined) => {
      let raw = value ?? undefined;
      if (raw && typeof raw !== 'string') {
        raw = String(raw.value);
      }

      if (identificationType?.value === IdentificationDocumentCode.Passport) {
        return undefined;
      }
      if (identificationType?.value === IdentificationDocumentCode.CL_RUT) {
        return validateRut(raw);
      }
      const typeRules = ((identificationType as unknown) as {
        rules?: {
          replacement?: Record<string, string>;
          min?: number;
          max?: number;
        };
      } | null)?.rules;
      if (typeRules && raw) {
        if (typeRules.replacement) {
          const patterns = Object.values(typeRules.replacement);
          const charPattern =
            patterns.length === 1 ? patterns[0] : `(?:${patterns.join('|')})`;
          const min = typeRules.min ?? 1;
          const max = typeRules.max ?? 20;
          const regex = new RegExp(`^${charPattern}{${min},${max}}$`);
          return regex.test(raw)
            ? undefined
            : t('common.validations.invalidFormat');
        }
      }

      return undefined;
    },
    [identificationType, validateRut, t],
  );

  const identificationTypeSelect = useMemo(() => {
    return (identificationTypeData?.identificationTypes ?? []).map((it) => ({
      value: it.id,
      label: it.name,
      rules: it.rules,
    }));
  }, [identificationTypeData]);

  const onSubmit = useCallback(
    async (values: SearchUserForm) => {
      setIsSearching(true);
      try {
        const res = await searchStudent({
          identificationTypeId: values.identificationType?.value
            ? String(values.identificationType.value)
            : '',
          identification: values.identification,
          countryId: values.country?.value
            ? String(values.country.value)
            : undefined,
        });
        if (res.data?.studentId) {
          history.push(`${PathsLayouts.currentAccount}/${res.data.studentId}`);
        }
      } catch {
        addToast({
          icon: 'error',
          color: 'danger',
          text: t(`${keyPrefix}.error.body`),
        });
      } finally {
        setIsSearching(false);
      }
    },
    [history, t],
  );

  return (
    <Container fluid className="p-4">
      <Form onSubmit={handleSubmit(onSubmit)}>
        <Row>
          <Col>
            <p>{t(`${keyPrefix}.title`)}</p>
          </Col>
        </Row>

        <Row className="px-3 align-items-start">
          <Col xs={12} lg={3} xl={2} className="pb-3">
            <OutlinedSelect
              label={t(`${keyPrefix}.identificationType`)}
              name="identificationType"
              options={identificationTypeSelect}
              isSearchable={false}
              control={control}
              rules={{ required: msgValidations.required }}
            />
          </Col>

          {identificationType?.value ===
            IdentificationDocumentCode.Passport && (
            <Col xs={12} lg={2} xl={2} className="pb-3">
              <OutlinedSelect
                label={t(`${keyPrefix}.country`)}
                name="country"
                isSearchable={true}
                placeholder={t(`${keyPrefix}.inputCountry`)}
                control={control}
                options={countryOptions}
                rules={{ required: msgValidations.required }}
                shouldUnregister
              />
            </Col>
          )}

          <Col xs={12} lg={4} xl={2} className="pb-3">
            <TextOutlinedInput
              label={t(`${keyPrefix}.identification`)}
              name="identification"
              control={control}
              rules={{
                required: msgValidations.required,
                validate: {
                  require: (value) => dynamicValidate(value),
                },
              }}
              shouldUnregister
            />
          </Col>

          <Col xs={12} lg={3} xl={3} className="pb-3">
            <div className="d-flex pt-4">
              <Button
                type="submit"
                text={t('common:actions.search')}
                size="md"
                className="ml-2"
                loading={isSearching}
                disabled={
                  Object.keys(formState.errors).length > 0 ||
                  !identification ||
                  isSearching
                }
              />
              <Button
                type="button"
                text={t('common:actions.clean')}
                size="md"
                className="ml-2"
                outlined
                onClick={() => {
                  reset({ ...DEFAULT_VALUES });
                }}
              />
            </div>
          </Col>
        </Row>
      </Form>
    </Container>
  );
}
